import { addToGroups, createNewGroup, removeFromGroups } from '@/apis/groups';
import { updatePathways } from '@/apis/pathways';
import { setSupporterStatus } from '@/apis/supporterstatuses';
import { addToTags, removeFromTags } from '@/apis/tags';
import { updateTasks } from '@/apis/tasks';
import { QFilter } from '@/types';
import { DomainCode, domainCodeAtom, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

export { useOtherActionsPanelList as useActionList } from '@components/OtherActionsPanelContent/shared/useOtherActionsPanelList';
export { useDeleteContactsFromList } from '@components/OtherActionsPanelContent/shared/useDeleteContactsFromList';

export const useAddContactsToGroups = () => {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useMutation({
    mutationFn: async ({ groupIds, qFilter }: { groupIds: number[]; qFilter: QFilter }) => {
      const token = await getAccessTokenSilently();
      return addToGroups(token, { domainCode, groupIds, qFilter });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Contacts added to group(s)');
    },
  });
};

export const useRemoveContactsFromGroups = () => {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useMutation({
    mutationFn: async ({ groupIds, qFilter }: { groupIds: number[]; qFilter: QFilter }) => {
      const token = await getAccessTokenSilently();
      return removeFromGroups(token, { domainCode, groupIds, qFilter });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Contacts removed from group(s)');
    },
  });
};

export const useCreateGroupsWithContacts = () => {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useMutation({
    mutationFn: async ({ groupNames, qFilter }: { groupNames: string[]; qFilter: QFilter }) => {
      const token = await getAccessTokenSilently();
      return createNewGroup(token, { domainCode, groupNames, qFilter });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Contacts added to new group(s)');
    },
  });
};

export const useAddTagsToContacts = () => {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useMutation({
    mutationFn: async ({ tagIds, qFilter }: { tagIds: number[]; qFilter: QFilter }) => {
      const token = await getAccessTokenSilently();
      return addToTags(token, { domainCode, tagIds, qFilter });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Tag(s) added to contacts');
    },
  });
};

export const useRemoveTagsFromContacts = () => {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useMutation({
    mutationFn: async ({ tagIds, qFilter }: { tagIds: number[]; qFilter: QFilter }) => {
      const token = await getAccessTokenSilently();
      return removeFromTags(token, { domainCode, tagIds, qFilter });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Tag(s) removed from contacts');
    },
  });
};

export const useSetRocketStatusForContact = () => {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useMutation({
    mutationFn: async ({ status, qFilter }: { status: string; qFilter: QFilter }) => {
      const token = await getAccessTokenSilently();
      return setSupporterStatus(token, { domainCode, status, qFilter });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Rocket status updated for contact(s)');
    },
  });
};

export const useUpdateTasksForContacts = () => {
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationFn: async (taskParameters: Parameters<typeof updateTasks>[1]) => {
      const token = await getAccessTokenSilently();
      return updateTasks(token, taskParameters);
    },
    onSuccess: () => enqueueSuccessNotification('Task status updated for contacts'),
    onError: () => enqueueErrorNotification('Task type invalid for selected election.'),
  });
};

export const useUpdatePathwayStepsForQFilter = () => {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationFn: async ({
      qFilter,
      domainCode,
      stepId,
      optionId,
    }: {
      qFilter: QFilter;
      domainCode: DomainCode;
      stepId: number;
      optionId: number;
    }) => {
      const token = await getAccessTokenSilently();
      return updatePathways(token, { qFilter, domainCode, stepId, optionId });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Pathway updated for contact(s)');
    },
  });
};
